import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useUserGet } from "hooks/citajteo/user/useUserGet";
import { useUserAdd } from "hooks/citajteo/user/useUserAdd";
import { useUserUpdate } from "hooks/citajteo/user/useUserUpdate";
import { QUERY_KEYS } from "constant/react_query";
import UserForm, {
  UserFormState,
} from "components/admin/common/user/UserForm/UserForm";
import { USER_TYPE } from "constant/user/user.constant";
import toast from "react-hot-toast";

const EMPTY_FORM: UserFormState = {
  un: "",
  email: "",
  firstName: "",
  lastName: "",
  uType: USER_TYPE.EDITOR,
  pw: "",
  pwRepeat: "",
};

const buildFormFromUser = (user: any): UserFormState => ({
  un: user.un ?? "",
  email: user.email ?? "",
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
  uType: user.uType ?? USER_TYPE.EDITOR,
  pw: "",
  pwRepeat: "",
});

const UserEdit: React.FC = () => {
  const navigate = useNavigate();
  const { itemId = "" } = useParams<{ itemId?: string }>();
  const user = useUserGet(itemId);
  const isEditMode = itemId !== "";
  const { userAddMutate } = useUserAdd({
    invalidateKeys: [QUERY_KEYS.CITAJTEO_USER_SEARCH],
  });
  const { userUpdateMutate } = useUserUpdate({
    invalidateKeysArray: [
      [QUERY_KEYS.CITAJTEO_USER_SEARCH],
      [QUERY_KEYS.CITAJTEO_USER_GET, itemId],
    ],
  });
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (!isEditMode) {
      setForm(EMPTY_FORM);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && user) {
      setForm(buildFormFromUser(user));
    }
  }, [isEditMode, user]);

  const handleChange =
    (field: keyof UserFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText("");

    const trimmedUserName = form.un.trim();
    const trimmedPassword = form.pw.trim();
    const trimmedPasswordRepeat = form.pwRepeat.trim();

    if (trimmedUserName === "") {
      setErrorText("Korisnicko ime ne sme biti prazno.");
      return;
    }

    if (isEditMode) {
      if (trimmedPassword !== "" && trimmedPassword !== trimmedPasswordRepeat) {
        setErrorText("Lozinka i ponovljena lozinka moraju biti iste.");
        return;
      }
    } else {
      if (trimmedPassword === "") {
        setErrorText("Lozinka ne sme biti prazna.");
        return;
      }
      if (trimmedPassword !== trimmedPasswordRepeat) {
        setErrorText("Lozinka i ponovljena lozinka moraju biti iste.");
        return;
      }
    }

    const updatePayload: {
      un: string;
      email: string;
      firstName: string;
      lastName: string;
      uType: string;
      pw?: string;
    } = {
      un: trimmedUserName,
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      uType: form.uType,
    };

    if (trimmedPassword !== "") {
      updatePayload.pw = trimmedPassword;
    }

    if (isEditMode) {
      userUpdateMutate(
        { id: itemId, updatePayload },
        {
          onSuccess: () => {
            if (itemId) {
              toast.success("Uspešno ažuriranje podataka korisnika.");
            }
            navigate("/admin-dashboard/user-li");
          },
          onError: (error) => {
            console.error("Error updating user:", error);
            toast.error("Greška prilikom ažuriranja podataka korisnika.");
          },
        },
      );
      return;
    }

    if (trimmedPasswordRepeat !== trimmedPassword) {
      return;
    }

    userAddMutate(
      {
        un: trimmedUserName,
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        uType: form.uType,
        pw: trimmedPassword,
      },
      {
        onSuccess: () => {
          navigate("/admin-dashboard/user-li");
        },
      },
    );
  };

  const handleReset = () => {
    if (isEditMode && user) {
      setForm(buildFormFromUser(user));
      setErrorText("");
      return;
    }

    setForm(EMPTY_FORM);
    setErrorText("");
  };

  if (isEditMode && !user) {
    return <FullWidth>Učitavanje korisnika...</FullWidth>;
  }

  return (
    <FullWidth>
      <div className="App">
        tip: {form.uType}
        <UserForm
          values={form}
          errorText={errorText}
          isEditMode={isEditMode}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onReset={handleReset}
        />
      </div>
    </FullWidth>
  );
};

export default UserEdit;

const FullWidth = styled.div`
  width: 100%;
`;
